import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import {
  Schedule,
  ScheduleStatus,
  ScheduleType
} from '@dadohr/shared/domainTypes/schedules';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  RowsRendererScrollContainer,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { Loader } from '../../../../../../../components/Loader';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useErrorLogger } from '../../../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import { updateDoc } from '../../../../../../../services/firebase';
import { useSchedulesByExperienceTemplateId } from '../../../../../../../services/schedules';
import {
  MomentRange,
  formatDatePrecise,
  toMoment
} from '../../../../../../../services/time';
import { JsonDialogInner } from '../../../../../../components/Json';
import { toFirestoreConsoleFromDoc } from '../../../../../../services/firebase';
import { publishTopic } from '../../../../../../services/pubsub';

type ColumnName =
  | 'experienceId'
  | 'status'
  | 'type'
  | 'channel'
  | 'at'
  | 'attempts'
  | 'finishedAt'
  | 'actions';

type D = Doc<Schedule>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'inspect',
        label: 'Inspect...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      },
      {
        key: 'execute',
        label: 'Execute',
        disabled:
          d.data.status !== ScheduleStatus.PENDING &&
          d.data.status !== ScheduleStatus.RETRY,
        onClick: () =>
          publishTopic({
            topic: 'schedules-executeSchedule',
            payload: {
              scheduleId: d.id,
              ts: Date.now()
            }
          })
      },
      {
        key: 'resetRetry',
        label: 'Reset retry attempts',
        disabled:
          d.data.status !== ScheduleStatus.ERROR &&
          d.data.status !== ScheduleStatus.RETRY,
        onClick: () =>
          updateDoc(d, () => ({ attempts: 0, status: ScheduleStatus.RETRY }))
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'experienceId',
    head: () => 'Exp Id',
    cell: (d) => d.data.experienceId,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => d.data.status,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'type',
    head: () => 'Type',
    cell: (d) => d.data.type,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 200
  },

  {
    key: 'channel',
    head: () => 'channel',
    cell: (d) =>
      d.data.type !== ScheduleType.EXPERIENCE_START && d.data.channel.type,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'at',
    head: () => 'at',
    cell: (d) => formatDatePrecise(toMoment(d.data.at)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'attempts',
    head: () => '#',
    cell: (d) => d.data.attempts,
    sortable: true,
    align: 'right',
    width: 50,
    flexGrow: 0
  },
  {
    key: 'finishedAt',
    head: () => 'finished',
    cell: (d) =>
      d.data.finishedAt ? formatDatePrecise(toMoment(d.data.finishedAt)) : null,
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];
const SORTERS: ItemSorters<D> = {
  attempts: {
    key: 'attempts',
    items: { sort: (d) => d.data.attempts, dir: 'desc' }
  },
  at: {
    key: 'at',
    items: { sort: (d) => d.data.at.toMillis(), dir: 'asc' }
  },
  type: {
    key: 'type',
    items: { sort: (d) => d.data.type, dir: 'asc' }
  },
  finishedAt: {
    key: 'at',
    items: { sort: (d) => d.data.finishedAt?.toMillis() || 0, dir: 'asc' }
  }
};

const DEFAULT_SORTER = SORTERS.at;
const DAYS_TO_LOOK_AHEAD = 21;

export const UpcomingSchedules = ({
  tpl
}: {
  tpl: Doc<IExperienceTemplate>;
}) => {
  const range: MomentRange = useMemo(() => {
    return {
      start: moment().startOf('d').subtract(1, 'd'),
      end: moment().startOf('d').add(DAYS_TO_LOOK_AHEAD, 'd')
    };
  }, []);

  const [ds, loading, err] = useSchedulesByExperienceTemplateId(
    tpl.data.orgId,
    tpl.id,
    range
  );

  useErrorLogger('schedules', err);

  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  return (
    <Section>
      <CanvasBar>
        <div>Recent Schedules (yesterday + next {DAYS_TO_LOOK_AHEAD} days)</div>
      </CanvasBar>
      {loading && <Loader height={500} />}
      {ds && (
        <RowsRendererScrollContainer height={500}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={ds}
            rowToKey={ID_TO_ROW_KEY}
            sorter={sorter || DEFAULT_SORTER}
            sortDirection={dir}
            renderHead={true}
            headProps={{ sticky: true }}
            onHeadClick={(c, d) =>
              setSort([SORTERS[c.key] || DEFAULT_SORTER, d])
            }
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={undefined}
          />
        </RowsRendererScrollContainer>
      )}
    </Section>
  );
};
